import React, { useEffect, useMemo, useState } from 'react';
import {
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  BulbOutlined,
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Alert, Avatar, Badge, Button, Card, Col, Empty, Input, Modal, Popconfirm, Row, Select, Space, Tag, Typography, message } from 'antd';
import { actionApi, ActionValidationError } from '../../../utils/actionApi';
import { getSuccessMessage } from '../../../utils/actionMessageTranslator';
import { employeesApi } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { toJalali } from '../../../utils/dateUtils';

const { Title, Text } = Typography;

const RACIRoles = [
  { value: 'مسئول_اجرا', label: 'مسئول اجرا', color: 'blue', icon: <ThunderboltOutlined /> },
  { value: 'پاسخگو', label: 'پاسخگو', color: 'gold', icon: <CrownOutlined /> },
  { value: 'مشاور', label: 'مشاور', color: 'purple', icon: <BulbOutlined /> },
  { value: 'مطلع', label: 'مطلع', color: 'green', icon: <EyeOutlined /> },
];

// Map برای کاربران ERP
const mapErpUserOption = (u) => ({
  value: u.id, // این id از ERP است
  label: (
    <Space>
      <Avatar size="small">{String(u.name || '').slice(0, 1) || '?'}</Avatar>
      <div className="leading-tight">
        <div style={{ fontFamily: 'CustomFont' }}>{u.name}</div>
        <div className="text-xs text-gray-500" style={{ fontFamily: 'CustomFont' }}>
          {u.email}
        </div>
      </div>
    </Space>
  ),
});

export default function ActionRaciTab({ actionId, primaryButtonClass, isActive = false }) {
  const [raciLoading, setRaciLoading] = useState(false);
  const [racis, setRacis] = useState([]);
  const [raciModalOpen, setRaciModalOpen] = useState(false);
  
  // State برای کاربران ERP
  const [erpEmployees, setErpEmployees] = useState([]);
  const [erpEmployeesLoading, setErpEmployeesLoading] = useState(false);
  
  // State برای انتخاب کاربر و نقش
  const [raciRole, setRaciRole] = useState(undefined);
  const [raciSelectedErpUserId, setRaciSelectedErpUserId] = useState(undefined); // این id از ERP است (تک کاربر)
  const [requiredAccountableErpId, setRequiredAccountableErpId] = useState(undefined);
  const [requiredResponsibleErpId, setRequiredResponsibleErpId] = useState(undefined);

  const hasRequiredRoles = useMemo(() => {
    const hasAccountable = racis.some((r) => r.raci_role === 'پاسخگو');
    const hasResponsible = racis.some((r) => r.raci_role === 'مسئول_اجرا');
    return hasAccountable && hasResponsible;
  }, [racis]);

  const loadRacis = async () => {
    if (!actionId) return;
    setRaciLoading(true);
    try {
      const res = await actionApi.getRacis(actionId);
      const list = res?.data || res?.data?.data || [];
      setRacis(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت RACI');
    } finally {
      setRaciLoading(false);
    }
  };

  useEffect(() => {
    if (isActive && actionId) {
      loadRacis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, isActive]);

  // دریافت لیست کارمندان از ERP
  const fetchErpEmployees = async (search = '') => {
    setErpEmployeesLoading(true);
    try {
      const filters = {};
      if (search) {
        filters.name = search;
      }
      const res = await employeesApi.getAll(filters);
      const list = (res.data || []).map((item) => ({
        id: item.id, // این id از ERP است
        name: item.name || '--',
        email: item.email || '--',
        employee_id: item.employee_detail?.employee_id || item.employeeDetail?.employee_id || null,
      }));
      setErpEmployees(list);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت لیست کارمندان');
    } finally {
      setErpEmployeesLoading(false);
    }
  };

  const openRaciModal = async () => {
    setRaciModalOpen(true);
    setRaciRole(undefined);
    setRaciSelectedErpUserId(undefined);
    setRequiredAccountableErpId(undefined);
    setRequiredResponsibleErpId(undefined);
    
    // دریافت لیست کارمندان ERP
    await fetchErpEmployees();
  };

  const closeRaciModal = () => {
    setRaciModalOpen(false);
    setRaciRole(undefined);
    setRaciSelectedErpUserId(undefined);
    setRequiredAccountableErpId(undefined);
    setRequiredResponsibleErpId(undefined);
    setErpEmployees([]);
  };

  const onChangeUser = (userId) => {
    setRaciSelectedErpUserId(userId);
    setRaciRole(undefined); // وقتی کاربر تغییر می‌کند، نقش را ریست می‌کنیم
  };

  const onChangeRole = (role) => {
    setRaciRole(role);
  };

  // همگام‌سازی کاربر از ERP و دریافت id جدید از Action
  const syncUserAndGetActionId = async (erpUserId) => {
    try {
      const res = await actionApi.syncUserFromErp(erpUserId);
      if (res?.success && res?.data?.id) {
        return res.data.id; // این id جدید از Action است
      } else {
        throw new Error(res?.message || 'خطا در همگام‌سازی کاربر');
      }
    } catch (error) {
      console.error('Sync user error:', error);
      throw error;
    }
  };

  const submitRaciModal = async () => {
    setRaciLoading(true);
    try {
      let assignments = [];

      if (racis.length === 0) {
        // باید همزمان پاسخگو + مسئول اجرا ارسال شوند
        if (!requiredAccountableErpId || !requiredResponsibleErpId) {
          message.error('برای ثبت اولیه، باید یک "پاسخگو" و یک "مسئول اجرا" انتخاب کنید');
          setRaciLoading(false);
          return;
        }

        // همگام‌سازی کاربر پاسخگو
        const accountableActionUserId = await syncUserAndGetActionId(requiredAccountableErpId);
        const accountable = { user_id: accountableActionUserId, raci_role: 'پاسخگو' };

        // همگام‌سازی کاربر مسئول اجرا
        const responsibleActionUserId = await syncUserAndGetActionId(requiredResponsibleErpId);
        const responsible = { user_id: responsibleActionUserId, raci_role: 'مسئول_اجرا' };

        assignments = [accountable, responsible];
      } else {
        if (!raciSelectedErpUserId) {
          message.error('ابتدا کاربر را انتخاب کنید');
          setRaciLoading(false);
          return;
        }
        if (!raciRole) {
          message.error('ابتدا نقش را انتخاب کنید');
          setRaciLoading(false);
          return;
        }

        // همگام‌سازی کاربر انتخاب شده
        const actionUserId = await syncUserAndGetActionId(raciSelectedErpUserId);
        assignments = [{ user_id: actionUserId, raci_role: raciRole }];
      }

      // ایجاد RACI با idهای جدید از Action
      const res = await actionApi.createRacis(actionId, assignments);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'RACI ثبت شد'));
        closeRaciModal();
        await loadRacis();
      } else {
        message.error(res?.message || 'خطا در ثبت RACI');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        message.error(error.message || 'خطا در اعتبارسنجی RACI');
      } else {
        message.error(error.message || 'خطا در ثبت RACI');
      }
    } finally {
      setRaciLoading(false);
    }
  };

  const deleteRaci = async (raciId) => {
    setRaciLoading(true);
    try {
      const res = await actionApi.deleteRaci(actionId, raciId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'RACI حذف شد'));
        await loadRacis();
      } else {
        message.error(res?.message || 'خطا در حذف RACI');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف RACI');
    } finally {
      setRaciLoading(false);
    }
  };

  return (
    <>
      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                تیم و نقش‌ها 
              </Title>
              <Badge count={racis.length} />
            </div>
            {/* <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
              مدیریت اعضای تیم و مسئولیت‌ها (مناسب برای توسعه‌های بعدی مثل پذیرش، پیشرفت و نظرات)
            </Text> */}
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openRaciModal}
            className={primaryButtonClass}
            style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
          >
            افزودن عضو
          </Button>
        </div>

        {!hasRequiredRoles && racis.length > 0 && (
          <Alert
            className="mt-4"
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message="نقش‌های ضروری کامل نیست"
            description={
              <Text style={{ fontFamily: 'CustomFont' }}>
                پیشنهاد: حداقل یک <Text strong>پاسخگو</Text> و یک <Text strong>مسئول اجرا</Text> داشته باشید.
              </Text>
            }
          />
        )}

        <div className="mt-4">
          {raciLoading ? (
            <div className="py-10">
              <TableSkeleton rows={3} columns={3} />
            </div>
          ) : racis.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ fontFamily: 'CustomFont' }}>هنوز عضوی اضافه نشده است</span>}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openRaciModal}
                style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                className="bg-green-600 hover:bg-green-700 border-0"
              >
                افزودن اولین اعضا
              </Button>
            </Empty>
          ) : (
            <div className="space-y-2">
              {racis.map((r) => {
                const roleMeta = RACIRoles.find((x) => x.value === r.raci_role);
                return (
                  <div
                    key={r.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-800"
                  >
                    <div className="flex items-center gap-2">
                      <Tag
                        color={roleMeta?.color || 'default'}
                        icon={roleMeta?.icon}
                        style={{ margin: 0, fontFamily: 'CustomFont' }}
                      >
                        {roleMeta?.label || r.raci_role}
                      </Tag>
                      <Text style={{ fontFamily: 'CustomFont' }}>
                        {r.user?.name || `کاربر #${r.user_id}`}
                      </Text>
                      {r.user?.email && (
                        <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                          ({r.user.email})
                        </Text>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      {/* <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                        #{r.id}
                      </Text> */}
                      {r.created_at && (
                        <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: '12px' }} className="flex items-center gap-1">
                          <CalendarOutlined className="text-[10px]" />
                          {(r.created_at) || r.created_at}
                        </Text>
                      )}
                      <Popconfirm
                        title="حذف RACI"
                        description="آیا از حذف این تخصیص مطمئن هستید؟"
                        okText="حذف"
                        cancelText="انصراف"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteRaci(r.id)}
                      >
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          style={{ borderRadius: 10, fontFamily: 'CustomFont' }}
                        />
                      </Popconfirm>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {RACIRoles.map((role) => (
            <Tag key={role.value} color={role.color} icon={role.icon} style={{ fontFamily: 'CustomFont' }}>
              {role.label}
            </Tag>
          ))}
        </div>
      </Card>

      <Modal
        title={
          <div style={{ fontFamily: 'CustomFont', fontSize: '18px', fontWeight: 600 }}>
            افزودن عضو (RACI)
          </div>
        }
        open={raciModalOpen}
        onCancel={closeRaciModal}
        footer={[
          <Button
            key="cancel"
            onClick={closeRaciModal}
            style={{ fontFamily: 'CustomFont', borderRadius: 10 }}
          >
            انصراف
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={submitRaciModal}
            loading={raciLoading}
            className={primaryButtonClass}
            style={{ fontFamily: 'CustomFont', borderRadius: 10 }}
          >
            ثبت
          </Button>,
        ]}
        width={600}
        style={{ fontFamily: 'CustomFont' }}
      >
        {erpEmployeesLoading ? (
          <div className="py-10">
            <TableSkeleton rows={3} columns={3} />
          </div>
        ) : racis.length === 0 ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={
                <span style={{ fontFamily: 'CustomFont' }}>ثبت اولیه RACI</span>
              }
              description={
                <span style={{ fontFamily: 'CustomFont', fontSize: '13px' }}>
                  برای اولین ثبت، باید یک پاسخگو و یک مسئول اجرا انتخاب شود. کاربران از پنل ERP انتخاب می‌شوند.
                </span>
              }
              style={{ borderRadius: 12 }}
            />

            <div>
              <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                انتخاب کاربر (پاسخگو)
              </Text>
              <Select
                allowClear
                value={requiredAccountableErpId}
                onChange={setRequiredAccountableErpId}
                placeholder="کاربر را از ERP انتخاب کنید"
                style={{ width: '100%', fontFamily: 'CustomFont', borderRadius: 10 }}
                options={erpEmployees.map(mapErpUserOption)}
                showSearch
                filterOption={(input, option) => {
                  const label = option?.label;
                  if (!label) return false;
                  const name = label?.props?.children?.[1]?.props?.children?.[0]?.props?.children || '';
                  const email = label?.props?.children?.[1]?.props?.children?.[1]?.props?.children || '';
                  const searchText = input.toLowerCase();
                  return name.toLowerCase().includes(searchText) || email.toLowerCase().includes(searchText);
                }}
                optionFilterProp="label"
                size="large"
              />
            </div>

            <div>
              <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                انتخاب نقش برای کاربر پاسخگو
              </Text>
              <Select
                value="پاسخگو"
                disabled
                style={{ width: '100%', fontFamily: 'CustomFont', borderRadius: 10 }}
                size="large"
              >
                <Select.Option value="پاسخگو">
                  <Tag color="gold" icon={<CrownOutlined />} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                    پاسخگو
                  </Tag>
                </Select.Option>
              </Select>
            </div>

            <div>
              <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                انتخاب کاربر (مسئول اجرا)
              </Text>
              <Select
                allowClear
                value={requiredResponsibleErpId}
                onChange={setRequiredResponsibleErpId}
                placeholder="کاربر را از ERP انتخاب کنید"
                style={{ width: '100%', fontFamily: 'CustomFont', borderRadius: 10 }}
                options={erpEmployees.map(mapErpUserOption)}
                showSearch
                filterOption={(input, option) => {
                  const label = option?.label;
                  if (!label) return false;
                  const name = label?.props?.children?.[1]?.props?.children?.[0]?.props?.children || '';
                  const email = label?.props?.children?.[1]?.props?.children?.[1]?.props?.children || '';
                  const searchText = input.toLowerCase();
                  return name.toLowerCase().includes(searchText) || email.toLowerCase().includes(searchText);
                }}
                optionFilterProp="label"
                size="large"
              />
            </div>

            <div>
              <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                انتخاب نقش برای کاربر مسئول اجرا
              </Text>
              <Select
                value="مسئول_اجرا"
                disabled
                style={{ width: '100%', fontFamily: 'CustomFont', borderRadius: 10 }}
                size="large"
              >
                <Select.Option value="مسئول_اجرا">
                  <Tag color="blue" icon={<ThunderboltOutlined />} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                    مسئول اجرا
                  </Tag>
                </Select.Option>
              </Select>
            </div>
          </Space>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                انتخاب کاربر
              </Text>
              <Select
                allowClear
                value={raciSelectedErpUserId}
                onChange={onChangeUser}
                placeholder="کاربر را از ERP انتخاب کنید"
                style={{ width: '100%', fontFamily: 'CustomFont', borderRadius: 10 }}
                options={erpEmployees.map(mapErpUserOption)}
                showSearch
                filterOption={(input, option) => {
                  const label = option?.label;
                  if (!label) return false;
                  const name = label?.props?.children?.[1]?.props?.children?.[0]?.props?.children || '';
                  const email = label?.props?.children?.[1]?.props?.children?.[1]?.props?.children || '';
                  const searchText = input.toLowerCase();
                  return name.toLowerCase().includes(searchText) || email.toLowerCase().includes(searchText);
                }}
                optionFilterProp="label"
                size="large"
              />
            </div>

            <div>
              <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                انتخاب نقش
              </Text>
              <Select
                value={raciRole}
                onChange={onChangeRole}
                placeholder={raciSelectedErpUserId ? 'نقش را انتخاب کنید' : 'ابتدا کاربر را انتخاب کنید'}
                disabled={!raciSelectedErpUserId}
                style={{ width: '100%', fontFamily: 'CustomFont', borderRadius: 10 }}
                options={RACIRoles.map((r) => ({
                  value: r.value,
                  label: (
                    <Tag color={r.color} icon={r.icon} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                      {r.label}
                    </Tag>
                  ),
                }))}
                size="large"
              />
            </div>
          </Space>
        )}
      </Modal>
    </>
  );
}
